import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { OrderSchemaType, ItemPath } from '../order.schema';

export interface PricePendency {
  kind: 'SERVICO_SEM_CUSTO' | 'SERVICO_SEM_MARKUP' | 'MP_SEM_CUSTO' | 'MP_SEM_MARKUP' | 'SEM_UNIDADE';
  label: string;
  /** Índice do subitem causador; undefined quando o causador é o próprio item. */
  subItemIndex?: number;
  stageId?: number;
  stockId?: number;
}

/** Versão pura — usada pelo hook (por item) e pela barra sticky (agregado do pedido). */
export const computeItemPendencies = (item: any): PricePendency[] => {
  const pendencies: PricePendency[] = [];
  if (!item) return pendencies;

    const unitId = Number(item.unitOfMeasurementId) || 0;
    const unitName = item.unitOfMeasurementName || 'na unidade do item';

    const candidates: { data: any; subItemIndex?: number }[] = [
      { data: item },
      ...(Array.isArray(item.subItems)
        ? item.subItems.map((s: any, i: number) => ({ data: s, subItemIndex: i }))
        : []),
    ];

    let needsUnit = false;

    candidates.forEach(({ data, subItemIndex }) => {
      if (!data) return;

      if (data.itemType === 'SERVICE' && data.stageId) {
        const name = data.stage?.servico || 'Serviço';
        if (!unitId) {
          needsUnit = true;
          return;
        }
        const hasCost = (Number(data.cost) || 0) > 0;
        const hasLegacyPrice = (Number(data.priceDetails?.unitPrice) || 0) > 0;
        const hasMarkup = data.markup !== undefined && data.markup !== null && data.markup !== '' && !isNaN(Number(data.markup));
        if (!hasCost && !hasLegacyPrice) {
          pendencies.push({ kind: 'SERVICO_SEM_CUSTO', label: `${name} — sem custo em ${unitName}`, subItemIndex, stageId: Number(data.stageId) });
        } else if (hasCost && !hasMarkup) {
          pendencies.push({ kind: 'SERVICO_SEM_MARKUP', label: `${name} — serviço sem markup`, subItemIndex, stageId: Number(data.stageId) });
        }
      }

      if (data.itemType === 'MATERIAL' && data.stockId) {
        const name = data.feedstock?.stockDescription || 'Estoque';
        if (!unitId) {
          needsUnit = true;
          return;
        }
        const hasPrice = (Number(data.priceDetails?.unitPrice) || 0) > 0;
        if (!hasPrice) {
          const stockCost = data.feedstock?.stockCostInUnit;
          const stockMarkup = data.feedstock?.stockMarkup;
          const hasCost = stockCost !== undefined && stockCost !== null && stockCost !== '' && !isNaN(Number(stockCost));
          const hasStockMarkup = stockMarkup !== undefined && stockMarkup !== null && stockMarkup !== '' && !isNaN(Number(stockMarkup));
          if (hasCost && !hasStockMarkup) {
            pendencies.push({ kind: 'MP_SEM_MARKUP', label: `${name} — estoque sem markup`, subItemIndex, stockId: Number(data.stockId) });
          } else {
            pendencies.push({ kind: 'MP_SEM_CUSTO', label: `${name} — sem custo em ${unitName}`, subItemIndex, stockId: Number(data.stockId) });
          }
        }
      }
    });

  if (needsUnit) {
    pendencies.unshift({ kind: 'SEM_UNIDADE', label: 'Selecione a Unidade do item para formar o preço' });
  }

  return pendencies;
};

export const useItemPricePendencies = (itemPath: ItemPath) => {
  const { control } = useFormContext<OrderSchemaType>();
  const item = useWatch({ control, name: itemPath as any }) as any;

  return useMemo(() => {
    const pendencies = computeItemPendencies(item);
    return { pendencies, isLocked: pendencies.length > 0 };
  }, [item]);
};
